/** Persist dashboard → editor entry (passport / resize categories) across reload. */

const STORAGE_KEY = "editor-entry-session";

export const ENTRY_SOURCE = {
  NORMAL: "normal",
  PASSPORT: "passport",
  MARKETPLACES: "marketplaces",
  DATING: "dating",
  SOCIAL_MEDIA: "social_media",
  EDIT_IMAGES_AI: "edit_images_ai",
  FOOD_CATALOG: "food_catalog",
  AUTOMOBILE_CATALOG: "automobile_catalog",
  PRODUCT_CATALOG: "product_catalog",
};

/** Trending-tool entry sources (standard editor in v1). */
export const TRENDING_ENTRY_SOURCES = [
  ENTRY_SOURCE.EDIT_IMAGES_AI,
  ENTRY_SOURCE.FOOD_CATALOG,
  ENTRY_SOURCE.AUTOMOBILE_CATALOG,
  ENTRY_SOURCE.PRODUCT_CATALOG,
];

export function isTrendingEntrySource(entrySource) {
  return TRENDING_ENTRY_SOURCES.includes(entrySource);
}

/** Dashboard resize card categories (not passport). */
export const RESIZE_ENTRY_CATEGORY_IDS = [3, 5, 6];

export const CATEGORY_ID_TO_ENTRY_SOURCE = {
  2: ENTRY_SOURCE.PASSPORT,
  3: ENTRY_SOURCE.MARKETPLACES,
  5: ENTRY_SOURCE.DATING,
  6: ENTRY_SOURCE.SOCIAL_MEDIA,
};

export function entrySourceForCategoryId(categoryId) {
  return CATEGORY_ID_TO_ENTRY_SOURCE[categoryId] ?? null;
}

export function isResizeEntrySource(entrySource) {
  return (
    entrySource === ENTRY_SOURCE.MARKETPLACES ||
    entrySource === ENTRY_SOURCE.DATING ||
    entrySource === ENTRY_SOURCE.SOCIAL_MEDIA
  );
}

export function readEditorEntrySession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.entrySource) return parsed;
    }
    // Legacy passport-only session (pre unified entry)
    const legacyRaw = sessionStorage.getItem("editor-passport-flow");
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      if (legacy?.editorFlow === "passport" && legacy?.passportPreset) {
        return {
          entrySource: ENTRY_SOURCE.PASSPORT,
          passportPreset: legacy.passportPreset,
        };
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function writeEditorEntrySession(payload) {
  if (!payload?.entrySource) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

export function clearEditorEntrySession() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function writePassportEntrySession(passportPreset) {
  if (!passportPreset) return;
  writeEditorEntrySession({
    entrySource: ENTRY_SOURCE.PASSPORT,
    passportPreset,
  });
}

export function writeResizeEntrySession(entrySource, resizePreset) {
  if (!resizePreset || !isResizeEntrySource(entrySource)) return;
  writeEditorEntrySession({
    entrySource,
    resizePreset,
  });
}

export function readPassportEntryFromSession() {
  const session = readEditorEntrySession();
  if (session?.entrySource !== ENTRY_SOURCE.PASSPORT || !session.passportPreset) {
    return null;
  }
  return {
    editorFlow: "passport",
    entrySource: ENTRY_SOURCE.PASSPORT,
    passportPreset: session.passportPreset,
  };
}

export function writeTrendingEntrySession(entrySource, resizePreset) {
  if (!entrySource || !isTrendingEntrySource(entrySource) || !resizePreset) return;
  writeEditorEntrySession({
    entrySource,
    resizePreset,
  });
}

export function readTrendingEntryFromSession() {
  const session = readEditorEntrySession();
  if (!session?.entrySource || !isTrendingEntrySource(session.entrySource)) {
    return null;
  }
  return {
    entrySource: session.entrySource,
    resizePreset: session.resizePreset ?? null,
  };
}

export function readResizeEntryFromSession() {
  const session = readEditorEntrySession();
  if (!session?.resizePreset || !isResizeEntrySource(session.entrySource)) {
    return null;
  }
  return {
    entrySource: session.entrySource,
    resizePreset: session.resizePreset,
  };
}

export function shouldApplyEntryResizePreset(entrySource, resizePreset) {
  if (!resizePreset) return false;
  if (isResizeEntrySource(entrySource)) return true;
  return isTrendingEntrySource(entrySource);
}

export function isPassportEntryActive(locationState) {
  if (
    locationState?.entrySource === ENTRY_SOURCE.PASSPORT ||
    locationState?.editorFlow === "passport"
  ) {
    return true;
  }
  return readPassportEntryFromSession() != null;
}

export function isResizeEntryActive(locationState) {
  if (locationState?.entrySource && isResizeEntrySource(locationState.entrySource)) {
    return Boolean(locationState?.entryResizePreset);
  }
  return readResizeEntryFromSession() != null;
}

export function resolveEntrySource(locationState, fallback = ENTRY_SOURCE.NORMAL) {
  if (locationState?.entrySource) return locationState.entrySource;
  if (locationState?.editorFlow === "passport") return ENTRY_SOURCE.PASSPORT;
  const session = readEditorEntrySession();
  if (session?.entrySource) return session.entrySource;
  return fallback;
}

/** Normalize API preset for handleResizePreset (expects `dimensions` string). */
export function normalizePresetForResizeEditor(preset) {
  if (!preset) return null;
  const w = Number(preset.width);
  const h = Number(preset.height);
  let dimensions = preset.dimensions ?? preset.dims_label ?? "";
  if (
    !dimensions &&
    Number.isFinite(w) &&
    Number.isFinite(h) &&
    w > 0 &&
    h > 0
  ) {
    dimensions = preset.aspect_ratio
      ? `${w} × ${h} (${preset.aspect_ratio})`
      : `${w} × ${h}`;
  }
  // Do not fall back to preset.label — that is the shape name now (Square), not dims.
  return { ...preset, dimensions };
}

/** Shared footer copy for Editing Tools prep overlays. */
export const TOOL_PREP_FOOTER_HINT =
  "Download the preview, open it in the editor, or close to return to the dashboard.";

/** Quiet empty-canvas copy while a source is preparing and no preview is ready yet. */
export const TOOL_PREP_PREPARING_PREVIEW_LABEL = "Preparing preview";

/** Footer copy after an AI result is ready in the preview. */
export const TOOL_PREP_RESULT_FOOTER_HINT =
  "Download the result, open it in the editor, or close to return to the dashboard.";

/** Tool prep overlay (Editing Tools; Document Tools; AI Tools; AI Photo Studio; Image Conversion). */
export const TOOL_PREP_KEYS = {
  RESIZE: "resize",
  SHADOW: "shadow",
  OUTLINE: "outline",
  BACKGROUND_COLOR: "background_color",
  WHITE_BACKGROUND: "white_background",
  BLACK_BACKGROUND: "black_background",
  ROTATE: "rotate",
  FLIP: "flip",
  PADDING: "padding",
  CROP: "crop",
  CIRCLE_CROP: "circle_crop",
  WATERMARK_MAKER: "watermark_maker",
  BLUR: "blur",
  GAUSSIAN_BLUR: "gaussian_blur",
  PIXEL_BLUR: "pixel_blur",
  MOTION_BLUR: "motion_blur",
  BOKEH_BLUR: "bokeh_blur",
  BACKGROUND_BLUR: "background_blur",
  PDF_WATERMARKER: "pdf_watermarker",
  WORD_WATERMARKER: "word_watermarker",
  PDF_PAGE_NUMBERS: "pdf_page_numbers",
  SIGN_PDF: "sign_pdf",
  AI_EXPAND: "ai_expand",
  AI_RECOLOR: "ai_recolor",
  AI_ENHANCE: "ai_enhance",
  AI_BACKGROUND_IMAGE: "ai_background_image",
  AI_SHADOW: "ai_shadow",
  AI_REMOVE_OBJECT: "ai_remove_object",
  AI_REMOVE_TEXT: "ai_remove_text",
  AI_UPSCALE: "ai_upscale",
  AI_RELIGHT: "ai_relight",
  AI_REMOVE_WATERMARK: "ai_remove_watermark",
  AI_BACKGROUND_REMOVER: "ai_background_remover",
  AI_SHADOW_REMOVER: "ai_shadow_remover",
  AI_OLD_PHOTO_RESTORE: "ai_old_photo_restore",
  AI_COLORIZER: "ai_colorizer",
  AI_UNBLUR: "ai_unblur",
  AI_REPLACE_OBJECT: "ai_replace_object",
  AI_REPLACE_TEXT: "ai_replace_text",
  AI_FACE_SWAP: "ai_face_swap",
  AI_PASSPORT_PHOTO_MAKER: "ai_passport_photo_maker",
  HEIC_TO_JPG: "heic_to_jpg",
  WEBP_TO_JPG: "webp_to_jpg",
  PNG_TO_JPG: "png_to_jpg",
  JPG_TO_PNG: "jpg_to_png",
  WEBP_TO_PNG: "webp_to_png",
};

export const TOOL_PREP_KEY_LIST = Object.values(TOOL_PREP_KEYS);

export function isToolPrepKey(key) {
  return TOOL_PREP_KEY_LIST.includes(key);
}

/** Dashboard → Editing Tools prep popups (resize, shadow, crop, …). */
export const EDITING_TOOL_PREP_KEYS = [
  TOOL_PREP_KEYS.RESIZE,
  TOOL_PREP_KEYS.SHADOW,
  TOOL_PREP_KEYS.OUTLINE,
  TOOL_PREP_KEYS.BACKGROUND_COLOR,
  TOOL_PREP_KEYS.WHITE_BACKGROUND,
  TOOL_PREP_KEYS.BLACK_BACKGROUND,
  TOOL_PREP_KEYS.ROTATE,
  TOOL_PREP_KEYS.FLIP,
  TOOL_PREP_KEYS.PADDING,
  TOOL_PREP_KEYS.CROP,
  TOOL_PREP_KEYS.CIRCLE_CROP,
  TOOL_PREP_KEYS.WATERMARK_MAKER,
  TOOL_PREP_KEYS.BLUR,
  TOOL_PREP_KEYS.GAUSSIAN_BLUR,
  TOOL_PREP_KEYS.PIXEL_BLUR,
  TOOL_PREP_KEYS.MOTION_BLUR,
  TOOL_PREP_KEYS.BOKEH_BLUR,
  TOOL_PREP_KEYS.BACKGROUND_BLUR,
];

export function isEditingToolPrepKey(key) {
  return EDITING_TOOL_PREP_KEYS.includes(key);
}

export const DOCUMENT_TOOL_PREP_KEYS = [
  TOOL_PREP_KEYS.PDF_WATERMARKER,
  TOOL_PREP_KEYS.WORD_WATERMARKER,
  TOOL_PREP_KEYS.PDF_PAGE_NUMBERS,
  TOOL_PREP_KEYS.SIGN_PDF,
];

export function isDocumentToolPrepKey(key) {
  return DOCUMENT_TOOL_PREP_KEYS.includes(key);
}

export const CONVERSION_TOOL_PREP_KEYS = [
  TOOL_PREP_KEYS.HEIC_TO_JPG,
  TOOL_PREP_KEYS.WEBP_TO_JPG,
  TOOL_PREP_KEYS.PNG_TO_JPG,
  TOOL_PREP_KEYS.JPG_TO_PNG,
  TOOL_PREP_KEYS.WEBP_TO_PNG,
];

export function isConversionToolPrepKey(key) {
  return CONVERSION_TOOL_PREP_KEYS.includes(key);
}

export function toolPrepWorkspacePath(toolKey) {
  return `/tools/${toolKey}`;
}

/** Prep overlays that include in-popup upload (Original card) — may open with no live file. */
export function toolPrepAllowsEmptySource(key) {
  return isToolPrepKey(key);
}

/** How the prep overlay was opened — Close destination depends on this. */
export const TOOL_PREP_ENTRY = {
  DASHBOARD: "dashboard",
  EDITOR: "editor",
};

export function resolveToolPrepEntryMode(locationState) {
  const fromState = locationState?.toolPrepEntryMode;
  if (
    fromState === TOOL_PREP_ENTRY.EDITOR ||
    fromState === TOOL_PREP_ENTRY.DASHBOARD
  ) {
    return fromState;
  }
  const session = readToolPrepSession();
  if (
    session?.entryMode === TOOL_PREP_ENTRY.EDITOR ||
    session?.entryMode === TOOL_PREP_ENTRY.DASHBOARD
  ) {
    return session.entryMode;
  }
  if (locationState?.openToolPrep) return TOOL_PREP_ENTRY.DASHBOARD;
  return TOOL_PREP_ENTRY.DASHBOARD;
}

const TOOL_PREP_STORAGE_KEY = "editor-tool-prep";

export function writeToolPrepSession(payload) {
  if (!payload?.toolPrep) return;
  try {
    sessionStorage.setItem(TOOL_PREP_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function readToolPrepSession() {
  try {
    const raw = sessionStorage.getItem(TOOL_PREP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.toolPrep ? parsed : null;
  } catch {
    return null;
  }
}

export function clearToolPrepSession() {
  try {
    sessionStorage.removeItem(TOOL_PREP_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Resolve which tool-prep overlay to open.
 *
 * Fresh navigations use `location.state` (dashboard upload → editor).
 * sessionStorage alone is NOT enough after a full reload: ImageContext loses the
 * File/blob, so reopening leaves an empty popup with no upload UI.
 * Honor the session only when a live image source is still available in-memory.
 */
export function resolveOpenToolPrep(locationState, options = {}) {
  const { hasLiveImageSource = false } = options;

  // Dashboard tools open as an in-page overlay; /tools/:toolKey deep-links redirect there.
  // Editor-origin prep still opens as an overlay on /editor.
  if (resolveToolPrepEntryMode(locationState) !== TOOL_PREP_ENTRY.EDITOR) {
    return null;
  }

  // Marketplace / category resize uses classic left panel — never tool prep.
  if (
    locationState?.openResizePanel ||
    locationState?.entryResizePreset ||
    isResizeEntrySource(locationState?.entrySource)
  ) {
    return null;
  }

  const fromLocation =
    locationState?.openToolPrep ||
    (() => {
      const panel =
        locationState?.editorPanel || locationState?.openEditorPanel || null;
      return isToolPrepKey(panel) ? panel : null;
    })();

  if (fromLocation) {
    return fromLocation;
  }

  const session = readToolPrepSession();
  if (!session?.toolPrep) {
    return null;
  }

  // Stale after reload / Done: no live upload left → drop session, stay on canvas.
  // Prep tools with in-popup upload may reopen without a live file.
  if (!hasLiveImageSource && !toolPrepAllowsEmptySource(session.toolPrep)) {
    clearToolPrepSession();
    return null;
  }

  return session.toolPrep;
}

/** Default object-shadow for Editing Tools → Shadow entry (visible, not zeroed). */
export const SHADOW_ENTRY_DEFAULT = {
  enabled: true,
  x: 17,
  y: -2,
  blur: 12,
  opacity: 0.7,
  color: "#000000",
};

/** Preview-only stage color in Shadow/Outline prep. */
export const SHADOW_ENTRY_BACKGROUND = "#FFFFFF";

/** Shared preview background mode for tool-prep cards (Shadow, Outline, BG color, …). */
export const TOOL_PREP_PREVIEW_BG = {
  WHITE: "white",
  /** Preview card for Background color / shade tools — shows the selected fill. */
  COLOR: "color",
  TRANSPARENT: "transparent",
  ORIGINAL: "original",
};

/**
 * First canvas after upload for every editing tool except Remove Background.
 * Shadow and outline start on the white Preview card. Color tools start on
 * their Preview card (the selected fill — white, unless the tool is black).
 * Remove Background keeps a transparent cutout and must not call this.
 */
export function initialToolPrepPreviewBg(accentMode) {
  if (accentMode === TOOL_PREP_PREVIEW_BG.COLOR) {
    return TOOL_PREP_PREVIEW_BG.COLOR;
  }
  return TOOL_PREP_PREVIEW_BG.WHITE;
}

/**
 * Map tool-prep stage preview (white / color / transparent) → editor canvas background.
 * Open-in-Editor must match whatever was visible on the tool canvas.
 * @returns {null | { type: 'color', color: string, imageUrl: null, aiConfig: null }}
 */
export function editorBackgroundFromToolPrepPreview(
  result = {},
  fallbackColor = "#FFFFFF"
) {
  const solid =
    result.backgroundColor ||
    (result.previewBg === TOOL_PREP_PREVIEW_BG.WHITE ||
    result.previewBg === TOOL_PREP_PREVIEW_BG.COLOR
      ? fallbackColor
      : null);
  if (!solid) return null;
  return {
    type: "color",
    color: solid,
    imageUrl: null,
    aiConfig: null,
  };
}

/** @deprecated Use TOOL_PREP_PREVIEW_BG */
export const SHADOW_PREVIEW_BG = TOOL_PREP_PREVIEW_BG;

/** Default object-outline for Editing Tools → Outline entry (visible stroke). */
export const OUTLINE_ENTRY_DEFAULT = {
  enabled: true,
  width: 2,
  blur: 0,
  color: "#000000",
};

export const OUTLINE_ENTRY_BACKGROUND = "#FFFFFF";
